import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { images } from "../data";
import { ChevronRight } from "lucide-react";

export default function Home() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt="Spisehuset i Værløse"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 text-center text-white px-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl lg:text-8xl font-thin font-serif tracking-[1px] mb-6 [text-shadow:_0_4px_24px_rgb(0_0_0_/_60%)]"
          >
            Spisehuset i Værløse
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-xl md:text-2xl font-normal italic mb-10 tracking-wide"
          >
            En oplevelse af nærvær, kvalitet og danske smage
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <Link
              to="/kontakt"
              className="px-10 py-4 border border-white hover:bg-white hover:text-dark transition-all duration-300 tracking-widest text-sm"
            >
              BESTIL BORD
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Focus Boxes Section */}
      <section className="pt-12 pb-16 md:py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-10 md:mb-24"
          >
            <h4 className="text-primary tracking-widest text-sm uppercase mb-4 font-medium">Udforsk</h4>
            <h2 className="text-4xl md:text-5xl font-serif text-dark">Vores Oplevelser</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
            {[
              {
                title: "Spisehuset",
                description: "Oplev vores passion for danske råvarer og klassisk kogekunst i hjertet af Værløse.",
                image: images.focus.spisehuset,
                link: "/spisehuset",
                cta: "GÅ TIL SPISEHUSET"
              },
              {
                title: "Selskaber & Events",
                description: "Lad os danne rammen om din næste fest. Vi skræddersyr arrangementet efter dine ønsker.",
                image: images.focus.selskaber,
                link: "/selskaber-og-events",
                cta: "SE SELSKABSLOKALER"
              },
              {
                title: "Catering & Take Away",
                description: "Tag Spisehuset med hjem. Vi tilbyder catering til private og virksomheder, samt daglig take away til afhentning.",
                image: images.focus.catering,
                link: "/takeaway-catering",
                cta: "UDFORSK CATERING & TAKE AWAY"
              }
            ].map((box, index) => (
              <motion.div
                key={box.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="group"
              >
                <Link to={box.link} className="block text-center">
                  <div className="relative md:aspect-[3/4] max-h-[200px] md:max-h-none overflow-hidden mb-6 md:mb-10">
                    <img 
                      src={box.image} 
                      alt={box.title} 
                      className="w-full h-[200px] md:h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-105" 
                    />
                    <div className="absolute inset-0 border-[1px] border-black/5 m-4 pointer-events-none" />
                  </div>
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 px-2">
                      <div className="h-[1px] flex-grow bg-primary/30" />
                      <h3 className="text-lg md:text-xl font-light tracking-[0.15em] font-serif uppercase text-center px-2 leading-snug">
                        {box.title}
                      </h3>
                      <div className="h-[1px] flex-grow bg-primary/30" />
                    </div>
                    <p className="text-gray-500 font-normal leading-relaxed max-w-[280px] mx-auto text-sm md:text-base italic">
                      {box.description}
                    </p>
                    <div className="pt-2">
                      <span className="inline-block text-[10px] tracking-[0.25em] font-normal text-dark border-b border-primary/20 group-hover:border-primary transition-all duration-500 pb-1 uppercase">
                        {box.cta}
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-12 md:py-16 bg-bg-light relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="max-w-5xl xl:max-w-none mx-auto"
          >
            <div className="flex flex-col">
              {/* Top: Title */}
              <div className="mb-12 md:mb-16">
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-primary tracking-[0.25em] text-xs uppercase font-medium">Om Os</span>
                  <div className="flex-grow h-[1px] bg-dark/10" />
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-dark tracking-wide leading-tight">
                  JJfoodsolutions i Værløse
                </h2>
              </div>
              
              {/* Bottom: Divided Text */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16 text-dark/80 font-normal leading-relaxed text-base md:text-lg">
                <div className="space-y-6">
                  <p>
                    Spisehuset v. JJfoodsolution er et moderne spisested med menukort som skifter løbende med sæson. Læs også mere om vores menu og sæsonbaserede retter, hvor vi præsenterer vores aktuelle udvalg og specialiteter.
                  </p>
                  <p>
                    Vores høje faglige niveau sikre at du som gæst eller arrangør af større arrangementer altid er sikret en god oplevelse med dig i fokus som vært eller gæst.
                  </p>
                </div>
                <div className="space-y-6">
                  <p>
                    Vores mange års erfaring i restauranter i København og som samarbejdspartner på store events har gjort vi kan sikre dit kommende arrangement hos os fra start til slut, du har stor indflydelse på dit arrangement hos os da vi har dig som vært/gæst i fokus og skræddersyer arrangementer om i er 20-75 personer som gæster vores hus kun 20 minutters kørsel fra København. Vi ser frem til at byde jer velkommen i vores hyggelige lokaler.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bento Grid Section */}
      <section className="py-12 md:py-16 bg-white relative">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 xl:auto-rows-[350px]">
            
            {/* Box 1: Take Away */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="md:col-span-1 xl:col-span-1 xl:row-span-1 py-8 md:p-8 flex flex-col justify-center items-start text-left group"
            >
              <h3 className="text-2xl font-serif text-dark mb-4">Take Away & Catering</h3>
              <p className="text-dark/70 font-normal mb-6 text-sm">
                Nyd Spisehusets velsmagende retter hjemme eller til festen. Vi tilbyder både daglig take away og catering.
              </p>
              <Link 
                to="/takeaway-catering" 
                className="inline-block text-[10px] tracking-[0.2em] uppercase font-normal border-b border-primary/40 pb-1 group-hover:border-primary transition-colors"
              >
                Læs mere
              </Link>
            </motion.div>

            {/* Box 2: Video */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="md:col-span-2 xl:col-span-2 xl:row-span-2 relative overflow-hidden bg-dark h-[200px] md:h-[350px] xl:h-full"
            >
              <video 
                className="absolute inset-0 w-full h-full object-cover" 
                autoPlay 
                muted 
                loop 
                playsInline
                src="https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/4b492988-759e-4cf0-bcaf-5b37497dc6bf.mp4"
              ></video>
              <div className="absolute inset-0 bg-black/10"></div>
            </motion.div>

            {/* Box 3: Image (Uploaded Screenshot) */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="md:col-span-1 xl:col-span-1 xl:row-span-1 relative overflow-hidden h-[200px] md:h-[350px] xl:h-full"
            >
              <img 
                src="https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/e7250693-36ff-4b98-a695-08a316a8d256.jpg" 
                alt="Stemning" 
                className="w-full h-full object-cover" 
              />
            </motion.div>

            {/* Box 4: Image */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="md:col-span-1 xl:col-span-1 xl:row-span-1 relative overflow-hidden h-[200px] md:h-[350px] xl:h-full"
            >
              <img 
                src="https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/b098cd89-3d6b-4fcd-874b-38a274f9e191.jpeg" 
                alt="Interiør" 
                className="w-full h-full object-cover" 
              />
            </motion.div>

            {/* Box 5: Image */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="md:col-span-1 xl:col-span-1 xl:row-span-1 relative overflow-hidden h-[200px] md:h-[350px] xl:h-full"
            >
              <img 
                src="https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/66231572-a1dc-4ceb-88ed-30b18d545642.jpg" 
                alt="Mad" 
                className="w-full h-full object-cover" 
              />
            </motion.div>

            {/* Box 6: Image (Moved up so the text box can be last) */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="md:col-span-1 xl:col-span-2 xl:row-span-1 relative overflow-hidden h-[200px] md:h-[350px] xl:h-full"
            >
              <img 
                src="https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/14bd10d8-ed6a-4e11-8c0d-d6f8a46efabe.jpeg" 
                alt="Restaurant" 
                className="w-full h-full object-cover" 
              />
            </motion.div>

            {/* Box 7: Last Text Box (Moved to end) */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="md:col-span-2 xl:col-span-2 xl:row-span-1 py-8 md:p-8 xl:p-10 flex flex-col justify-center items-start text-left"
            >
              <h4 className="text-primary text-[10px] md:text-xs tracking-[0.25em] uppercase font-normal mb-3">
                Mere end et spisested
              </h4>
              <h3 className="text-2xl md:text-3xl font-serif text-dark mb-4 leading-tight">
                God mad med kvalitetsråvarer i en afslappet atmosfære
              </h3>
              <p className="text-dark/70 font-normal mb-8 text-sm leading-relaxed max-w-xl">
                Spisehuset i Værløse Golfklub er et populært mødested for både golfspillere og lokalbefolkningen, der ønsker at nyde god mad og en afslappet atmosfære. Udover den lækre mad og drikke tilbyder restaurant Spisehuset en afslappet og venlig atmosfære.
              </p>
              <div>
                <Link 
                  to="/kontakt" 
                  className="inline-block px-8 py-4 bg-primary text-white text-[10px] tracking-[0.2em] uppercase hover:bg-dark transition-colors duration-300"
                >
                  Kontakt os
                </Link>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Contact Form Section with Parallax Background */}
      <section className="relative py-12 md:py-32 bg-fixed bg-center bg-cover" style={{ backgroundImage: 'url("https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/4ca9bd25-58d1-4a76-9ca3-8de3589d8014.jpg")' }}>
        <div className="absolute inset-0 bg-black/60" />
        <div className="container mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-white px-4 md:px-8"
          >
            <div className="text-center mb-8">
              <h4 className="text-primary text-sm tracking-widest uppercase mb-4">
                Kontakt os gerne for booking af bord eller ønsker til dit arrangement
              </h4>
              <h2 className="text-3xl md:text-4xl font-serif mb-3">Har du nogle spørgsmål?</h2>
              <p className="text-white/80 font-normal leading-relaxed text-sm md:text-base">
                Hos Spisehuset i Værløse nyder vi at høre fra vores kunder. Uanset om du har spørgsmål, vil bestille catering, høre mere om vores take away eller noget helt fjerde, så er du velkommen til at kontakte os med dine spørgsmål.
              </p>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const data = Object.fromEntries(formData.entries());
                
                try {
                  await fetch("https://api.dev.auxo.dk/webform/submit", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(data),
                  });
                  alert("Tak for din henvendelse. Vi vender tilbage hurtigst muligt!");
                  e.target.reset();
                } catch (error) {
                  console.error("Fejl ved afsendelse:", error);
                  alert("Der skete en fejl. Prøv venligst igen senere.");
                }
              }}
              className="space-y-4"
            >
              {/* Honeypot field (Anti-spam) */}
              <div className="sr-only absolute w-0 h-0 overflow-hidden" aria-hidden="true">
                <label htmlFor="website_url">Website URL</label>
                <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-[10px] uppercase tracking-wider text-white/80 mb-1">Navn</label>
                  <input required type="text" id="name" name="navn" className="w-full border-b border-white/30 py-1 focus:outline-none focus:border-primary transition-colors bg-transparent text-white" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-[10px] uppercase tracking-wider text-white/80 mb-1">E-mail</label>
                  <input required type="email" id="email" name="email" className="w-full border-b border-white/30 py-1 focus:outline-none focus:border-primary transition-colors bg-transparent text-white" />
                </div>
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-[10px] uppercase tracking-wider text-white/80 mb-1">Telefon</label>
                <input type="tel" id="phone" name="telefon" className="w-full border-b border-white/30 py-1 focus:outline-none focus:border-primary transition-colors bg-transparent text-white" />
              </div>

              <div>
                <label htmlFor="message" className="block text-[10px] uppercase tracking-wider text-white/80 mb-1">Besked</label>
                <textarea required id="message" name="besked" rows="3" className="w-full border-b border-white/30 py-1 focus:outline-none focus:border-primary transition-colors bg-transparent resize-none text-white"></textarea>
              </div>

              <div className="pt-4 text-center">
                <button type="submit" className="px-10 py-4 bg-primary text-white text-[10px] tracking-[0.2em] uppercase hover:bg-dark transition-colors duration-300 w-full md:w-auto">
                  Send besked
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
